/**
 * Central runtime config. Never hardcode the app name or URL root elsewhere —
 * read from here so a single env change re-brands / re-roots the whole app.
 */

import { DEFAULT_APP_NAME } from "../brand.config";

const env = import.meta.env;

export const config = {
  /**
   * Build-time display name. Only a fallback: the authoritative name arrives
   * from `/meta/config` and is what every surface actually renders. This is
   * what shows before that response lands, or if the API is unreachable.
   */
  // `||`, not `??`: an unset Docker build arg arrives as an empty string, which
  // `??` would happily accept and render as a nameless header.
  appName: (env.VITE_APP_NAME as string | undefined) || DEFAULT_APP_NAME,

  /** URL root the SPA is served under. Vite injects `BASE_URL` from `base`, which
   *  vite.config.ts takes from APP_ROOT — the one value nginx and the API share. */
  urlRoot: env.BASE_URL,

  /** Base URL for the JSON API: same-origin `<urlRoot>api` unless overridden for a
   *  split-origin deployment. `||`, not `??`: an unset Docker build arg is "". */
  apiBase: (env.VITE_API_BASE as string | undefined) || `${env.BASE_URL}api`,

  /**
   * Basemap preference for the sensor maps. `"auto"` (default) picks a
   * theme-matched CARTO vector basemap (Positron light / Dark-Matter dark) and
   * swaps live on theme toggle. May also be a concrete BasemapId
   * (`carto-positron` | `carto-dark` | `carto-voyager` | `esri-satellite` |
   * `offline`). `offline` renders no basemap tiles (data-sovereign / firewalled
   * deployments) and is the automatic fallback when a CDN basemap fails.
   * The basemap catalog + style URLs live in `components/map/basemaps.ts`.
   */
  mapBasemap: (env.VITE_MAP_BASEMAP as string | undefined) ?? "auto",

  /** Default map focus — Johar Town DMA, Lahore. */
  mapCenter: { lng: 74.3001, lat: 31.4683 },
  mapZoom: 13,
} as const;

export type AppConfig = typeof config;
